import React, { useEffect, useState } from "react";
import { graphql, navigate, useStaticQuery } from "gatsby";
import { Form, FormGroup } from "react-bootstrap";
import PropTypes from "prop-types";

import NotificationAlert from "../notifications/notification-alert";

import PageSearchBar from "../search-bar/page-search-bar";
import BankersSearchBar from "../search-bar/bankers-search-bar";
import MegaMenuSearchBar from "../search-bar/mega-menu-search-bar";

// Null import when ssr build is done, it fixes the module error trying to access window object.
const AddSearchClient = typeof window !== `undefined` ? require("addsearch-js-client") : null;

/**
 * This component renders the search suggestions input with 3 different versions.
 *
 * @note TODO: This component needs to be refactored and easy to read, we know what it does now and its use cases so we can cover the whole process and make it more efficient.
 *
 * @param {Object} props - The object containing props.
 * @param {"mobile" | "modal" | "widget" | "select-banking"} props.type - required string to render the component depending the page.
 * @param {string} props.query - text/state to send as query parameter to the `/customer-service/search?addsearch=${query}` link and addSearchClient.sendStatsEvent("search", query, { numberOfResults: 1 }).
 * @param {function} props.setQuery - state function from the parent to have access and customize the setQuery if needed.
 * @param {Object} props.querySuggestions - object with the array of hits/results from the addsearchapi/database depending the case.
 * @param {Object[]} props.querySuggestions.hits - array of objects with the results from addsearchapi/database depending the case.
 * @param {function} props.setQuerySuggestions - state function from the parent to have access and customize the setQuerySuggestions if needed.
 * @param {function} props.handleCloseSearchModal - if type "modal" and we want to reset the values or do something else when closing the search modal we need this function.
 * @param {boolean} props.showMobileSearch - when using type "mobile" we need to know if this is true or false to use different functions or focus the search input when changes to true.
 * @param {function} props.setShowMobileSearch - state function to trigger changes in the parent from here.
 * @param {function} props.handleCloseMobileSearch - when using type "mobile" we use this function to clean the values from the search component and modal.
 * @param {string} props.search - this state help us to avoid repeated queries or unnecessary state updates for the same query text.
 * @param {function} props.setSearch - when the query and search states are different we can update the search state and perform a search.
 * @param {function} props.setSelectBankers - when type "select-banking" we need this function to update the select bankers with the results from the suggestions.
 * @returns {React.JSX.Element} Returns the JSX component to render.
 */
const SearchSuggestionsInput = ({
  type = "modal",
  query = "",
  setQuery = () => {},
  querySuggestions = { hits: [] },
  setQuerySuggestions = () => {},
  handleCloseSearchModal = () => {},
  showMobileSearch = false,
  setShowMobileSearch = () => {},
  handleCloseMobileSearch = () => {},
  search = "",
  setSearch = () => {},
  setSelectBankers = () => {}
}) => {
  const [searchType, setSearchType] = useState(0); // 0 For main search type and 1 for secondary search type
  const [shouldFocus, setShouldFocus] = useState(false);
  const [shouldBlur, setShouldBlur] = useState(false);

  const addSearchCallback = (res) => {
    // console.log("THE RESULTS: ", res.hits);
    setQuerySuggestions({
      ...res,
      hits: res.hits.filter((element) => element.title != null)
    });
  };

  useEffect(() => {
    if (showMobileSearch) {
      setShouldFocus(true);
    }
  }, [showMobileSearch]);

  useEffect(() => {
    if (type === "modal") {
      setShouldFocus(true);
    } else {
      setShouldBlur(true);
    }
  }, []);

  const BRANCH_STATES_AND_SELECT_BANKERS = useStaticQuery(graphql`
    {
      allMongodbWebsiteBranchStates(sort: { StateName: ASC }) {
        edges {
          node {
            branchStateId
            StateName
            StateCode
          }
        }
      }
      allMongodbWebsiteSelectBankers {
        edges {
          node {
            selectBankerId
            State
            Name
            PhoneNumber
            EmailAddress
          }
        }
      }
    }
  `);

  const stateList = BRANCH_STATES_AND_SELECT_BANKERS.allMongodbWebsiteBranchStates.edges;
  const bankersList = BRANCH_STATES_AND_SELECT_BANKERS.allMongodbWebsiteSelectBankers.edges;

  // TODO: why are we using edges[0].node instead of node[0]?
  const handleRadioChange = (e) => {
    setSelectBankers([]);
    setShouldBlur(true);

    if (searchType === 1) {
      let firstState = geoState != null ? geoState : stateList[0].node.StateName;
      setSearchType(0);
      setQuery(firstState);
      setSearch(firstState);
      searchSelectBankers(firstState, 0);
    } else {
      setSearchType(1);
      setQuery("");
      setSearch("");
    }
  };

  let [error, setError] = useState(null);

  const buildErrorContainer = function () {
    if (error !== null) {
      return <NotificationAlert bodyText={error} type="danger" />;
    }
  };

  const [geoState, setGeoState] = useState(null);
  const [isUtagJsLoaded, setIsUtagJsLoaded] = useState(false);

  useEffect(() => {
    addSearchClient.setCollectAnalytics(false);
    if (type === "select-banking") {
      function handleUtagScriptLoad() {
        if (typeof window?.utag?.view === "function") {
          setIsUtagJsLoaded(true);
        }
      }
      const utagScript = document.getElementById("utag-script");
      utagScript.addEventListener("load", handleUtagScriptLoad);
      return () => utagScript.removeEventListener("load", handleUtagScriptLoad);
    }
  }, []);

  useEffect(() => {
    if (type === "select-banking") {
      if (!isUtagJsLoaded) return;

      let script = document.createElement("script");

      script.id = "tealium-utag-view-gatsby";

      document.body.appendChild(script);

      let searchState = window.utag.data.geo_state != null ? window.utag.data.geo_state.toLowerCase() : "";

      // let validStateFound = validStates.find((state) => state === searchState);
      let validStateFound = stateList.find(
        (state) => state.node.StateCode?.toLowerCase() === searchState.toLowerCase()
      );
      let finalState;
      if (validStateFound != null) {
        setGeoState(validStateFound.node.StateName);
        finalState = validStateFound.node.StateName;
      } else {
        finalState = stateList[0].node.StateName;
      }
      setQuery(finalState);
      setSearch(finalState);
      searchSelectBankers(finalState, searchType);

      // cleanup on page tear down
      return (el) => {
        document.body.removeChild(script);
      };
    }
  }, [isUtagJsLoaded]);

  const searchSelectBankersSuggestions = (searchValue) => {
    let queryPro = bankersList.filter((banker) => banker.node.Name.toLowerCase().includes(searchValue.toLowerCase()));

    return queryPro;
  };

  const searchSelectBankers = (searchValue, searchTypePro) => {
    // console.log("THE SV: " + searchValue + "\n\n" + searchTypePro);
    if (searchTypePro === 0) {
      let queryPro = [];
      if (searchValue === "NewMexico") {
        queryPro = bankersList.filter(
          (banker) =>
            banker.node.State.toLowerCase().includes("newmexico") ||
            banker.node.State.toLowerCase().includes("new mexico")
        );
      } else {
        queryPro = bankersList.filter((banker) => banker.node.State.toLowerCase().includes(searchValue.toLowerCase()));
      }

      setError(
        queryPro.length > 0
          ? null
          : 'This search returned 0 results. For regions without a designated Select Banker, reach out to <a href="mailto:selectbanking@wafd.com" id="error-select-banking-email">selectbanking@wafd.com</a>'
      );
      setSelectBankers(queryPro);
    } else {
      let queryPro = bankersList.filter((banker) => banker.node.Name.toLowerCase().includes(searchValue.toLowerCase()));

      setError(queryPro.length > 0 ? null : "This search returned 0 results. Try rewording your search.");
      setSelectBankers(queryPro);
    }
  };

  const PUBLIC_API_TOKEN = "32c2d1fdc8fe47dfd59c772e4a8a9263";
  // Null client when SSR build is done, it fixes the error coming from using a null import
  const addSearchClient = AddSearchClient ? new AddSearchClient(PUBLIC_API_TOKEN) : null;

  const clearQuery = () => setQuery("");

  return type === "modal" || type === "mobile" ? (
    <MegaMenuSearchBar
      type={type}
      query={query}
      clearQuery={clearQuery}
      querySuggestions={querySuggestions}
      shouldFocus={shouldFocus}
      shouldFocusCallback={() => setShouldFocus(false)}
      onHideMobileSearch={(event) => {
        event.preventDefault();
        setShowMobileSearch(false);
        setQuerySuggestions({ hits: [] });
        setQuery("");
      }}
      onInputChange={(event) => {
        setQuery(event.target.value);
        if (event.target.value.length > 0) {
          // addSearchClient.setSuggestionsSize(ITEMS_PER_PAGE);
          addSearchClient.search(event.target.value, addSearchCallback);
        }
      }}
      onInputKeyDown={(event) => {
        if (event.key === "Enter") {
          if (showMobileSearch) {
            handleCloseMobileSearch(false);
          } else {
            handleCloseSearchModal();
          }
          if (query.length > 0) {
            navigate(`/customer-service/search?addsearch=${query}`);
            addSearchClient.sendStatsEvent("search", query, { numberOfResults: 1 });
            setQuery("");
            setQuerySuggestions({ hits: [] });
          }
        }
      }}
      onSearchButtonClick={(event) => {
        event.preventDefault();
        if (query !== search) {
          if (query !== "") {
            handleCloseSearchModal();
            addSearchClient.sendStatsEvent("search", query, { numberOfResults: 1 });
            navigate(`/customer-service/search?addsearch=${query}`);
          }
        }
      }}
      onPopulateQueryButtonClick={(newQuery) => {
        setQuery(newQuery);
        addSearchClient.search(newQuery, addSearchCallback);
      }}
      onResultSearchButtonClick={(suggestionText, suggestionId, index) => {
        if (showMobileSearch) {
          handleCloseMobileSearch(false);
        } else {
          handleCloseSearchModal();
        }

        setQuery(suggestionText);
        addSearchClient.search(suggestionText, () => {});
        setQuerySuggestions({ hits: [] });
        navigate(`/customer-service/search?addsearch=${suggestionText}`);

        addSearchClient.sendStatsEvent("search", suggestionText, { numberOfResults: 1 });
        addSearchClient.sendStatsEvent("click", suggestionText, {
          documentId: suggestionId,
          position: index
        });
      }}
    />
  ) : type === "widget" ? (
    <div className="row position-relative">
      <PageSearchBar
        query={query}
        clearQuery={clearQuery}
        querySuggestions={querySuggestions}
        shouldFocus={shouldFocus}
        shouldFocusCallback={() => setShouldFocus(false)}
        shouldBlur={shouldBlur}
        shouldBlurCallback={() => setShouldBlur(false)}
        onPopulateQueryButtonClick={(newQuery) => {
          setQuery(newQuery.toLowerCase());
          addSearchClient.search(newQuery, addSearchCallback);
        }}
        onSearch={(newQuery, suggestionId, index) => {
          if (newQuery !== search.replaceAll(" | WaFd Bank", "").toLowerCase()) {
            const isResultSearch = suggestionId !== undefined && index !== undefined;

            setQuery(newQuery);
            setSearch(newQuery);
            addSearchClient.search(newQuery, () => {});
            addSearchClient.sendStatsEvent("search", newQuery, { numberOfResults: 1 });

            if (isResultSearch) {
              addSearchClient.sendStatsEvent("click", newQuery, {
                documentId: suggestionId,
                position: index
              });
            }

            setShouldBlur(true);
          }
        }}
        onInputChange={(event) => {
          setQuery(event.target.value);
          setShouldFocus(true);
          if (event.target.value.length > 0) {
            addSearchClient.search(event.target.value, addSearchCallback);
          }
        }}
        onInputKeyDown={(event) => {
          if (event.key === "Enter" && query.length > 0) {
            event.preventDefault();
            if (query !== search) {
              setSearch(query);
              setShouldBlur(true);
              addSearchClient.sendStatsEvent("search", query, { numberOfResults: 1 });
            }
          }
        }}
      />
    </div>
  ) : type === "select-banking" ? (
    <div className="row position-relative">
      <div>
        <h4 className="mt-2 fw-semi-bold" style={{ paddingLeft: 3 }}>
          Search for a Select Banker
        </h4>
        <FormGroup>
          <Form.Check
            tabIndex={0}
            inline
            label="Search By Location"
            className="pl-0"
            name="principal"
            type={"radio"}
            id={`inline-radio-1`}
            value={0}
            checked={searchType === 0}
            onChange={(e) => handleRadioChange(e)}
          />
          <Form.Check
            tabIndex={0}
            inline
            label="Search By Banker Name"
            className="pl-0"
            name="secondary"
            type={"radio"}
            id={`inline-radio-2`}
            style={{ marginTop: 0, marginRight: 0 }}
            value={1}
            checked={searchType === 1}
            onChange={(e) => handleRadioChange(e)}
          />
        </FormGroup>
        <div className={`col-12 col-sm-10 col-md-8 col-lg-7 col-xl-6 px-0 border-radius-6`}>
          {buildErrorContainer()}
        </div>
      </div>
      <BankersSearchBar
        query={query}
        clearQuery={clearQuery}
        querySuggestions={querySuggestions}
        searchType={searchType}
        onFocus={(event) => {
          if (event.target.value.length > 0) {
            setQuerySuggestions({
              hits: searchSelectBankersSuggestions(event.target.value)
            });
          }
          setShouldFocus(true);
        }}
        shouldFocus={shouldFocus}
        shouldFocusCallback={() => setShouldFocus(false)}
        shouldBlur={shouldBlur}
        shouldBlurCallback={() => setShouldBlur(false)}
        states={stateList}
        onStateSearch={(newState) => {
          if (newState !== search) {
            setQuery(newState);
            setSearch(newState);
            searchSelectBankers(newState, 0);
          }
        }}
        onInputChange={(event) => {
          setQuery(event.target.value);
          setShouldFocus(true);
          if (event.target.value.length > 0) {
            setQuerySuggestions({
              hits: searchSelectBankersSuggestions(event.target.value)
            });
          }
        }}
        onInputKeyDown={(event) => {
          if (event.key === "Enter" && query.length > 0) {
            event.preventDefault();
            if (query !== search) {
              setSearch(query);
              setShouldBlur(true);
              searchSelectBankers(query, 1);
            }
          }
        }}
        onBankerSearch={(newQuery) => {
          if (newQuery !== search && newQuery !== "") {
            setQuery(newQuery);
            setSearch(newQuery);
            addSearchClient.sendStatsEvent("search", newQuery, { numberOfResults: 1 });
            setShouldBlur(true);
            searchSelectBankers(newQuery, 1);
          }
        }}
        onPopulateQueryButtonClick={(newQuery) => {
          setQuery(newQuery);
          setQuerySuggestions({
            hits: searchSelectBankersSuggestions(newQuery)
          });
        }}
      />
    </div>
  ) : null;
};

export default SearchSuggestionsInput;

SearchSuggestionsInput.propTypes = {
  type: PropTypes.oneOf(["mobile", "modal", "widget", "select-banking"]).isRequired,
  query: PropTypes.string.isRequired,
  setQuery: PropTypes.func.isRequired,
  querySuggestions: PropTypes.shape({
    hits: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
        node: PropTypes.shape({ Name: PropTypes.string.isRequired })
      })
    ).isRequired
  }).isRequired,
  setQuerySuggestions: PropTypes.func.isRequired,
  handleCloseSearchModal: PropTypes.func,
  showMobileSearch: PropTypes.bool,
  setShowMobileSearch: PropTypes.func,
  handleCloseMobileSearch: PropTypes.func,
  search: PropTypes.string.isRequired,
  setSearch: PropTypes.func,
  setSelectBankers: PropTypes.func
};
